import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { promptDispatchAssets } from "@/components/order/DispatchAssetDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityPicker } from "@/components/app/EntityPicker";
import { StatusBadge } from "@/components/app/common";
import { BudgetCompare } from "@/components/planning/BudgetCompare";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { SerialCapture, cleanSerials, useSerialFlags } from "@/components/logistics/SerialCapture";
import { ChallanFields } from "@/components/logistics/ChallanFields";

const trRender = (t) => ({ title: t.name, subtitle: t.code });
const FIELDS = [["movement_date", "Movement date *", "date"], ["vehicle_no", "Vehicle no."], ["driver_name", "Driver"], ["driver_phone", "Driver phone"], ["lr_no", "LR no."],
  ["lr_date", "LR date", "date"], ["eway_bill_no", "E-way bill no."], ["from_location", "From (city)"], ["expected_delivery_date", "Expected delivery", "date"], ["notes", "Notes"]];

// What the movement will carry - all from the plans, nothing re-typed; serials captured here for challan dispatches
function Summary({ plans, dc, serials, setSerials }) {
  const tracked = useSerialFlags(plans.flatMap((p) => p.lines.map((l) => l.part_code)));
  return (
    <div className="space-y-2" data-testid="mfp-summary">
      {plans.map((p) => (
        <div key={p.id} className="rounded border border-slate-200 px-3 py-2 text-xs">
          <div className="flex flex-wrap gap-x-3 font-semibold"><span className="font-mono">{p.planned_dispatch_number}</span><span className="font-mono">{p.lm_number}</span><span>{p.customer_snapshot?.name}</span><span className="text-slate-500 font-normal">{fmtDate(p.expected_dispatch_date)}</span><StatusBadge status={p.qc_status} testid={`mfp-qc-${p.planned_dispatch_number}`} /></div>
          <div className="text-slate-600">{p.dispatch_address_snapshot?.address_label} · {p.dispatch_address_snapshot?.city}</div>
          <div className="text-slate-600">{p.lines.filter((l) => l.balance_qty > 0).map((l) => `${l.part_code || l.item_description} × ${l.balance_qty} ${l.uom || ""}`).join(", ")}</div>
          <div className="font-mono text-sky-800">Invoice: {p.invoices.map((i) => i.invoice_no).join(", ") || "none linked"}</div>
          {p.lines.filter((l) => l.balance_qty > 0 && tracked.has(l.part_code)).map((l) => (
            <div key={l.id} className="mt-1"><div className="text-[11px] font-semibold">{l.part_code || l.item_description} × {l.balance_qty}</div>
              <SerialCapture qty={l.balance_qty} tracked={tracked.has(l.part_code)} rows={serials[`${p.id}|${l.line_item_id}`]} onChange={(rows) => setSerials({ ...serials, [`${p.id}|${l.line_item_id}`]: rows })} testid={`mfp-serials-${p.planned_dispatch_number}-${l.line_number || l.id}`} /></div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function MovementFromPlansDialog({ plans, onClose, onDone }) {
  const [f, setF] = useState({ movement_date: new Date().toISOString().slice(0, 10) });
  const [tr, setTr] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [est, setEst] = useState({});
  const [serials, setSerials] = useState({});
  const notQc = plans.filter((p) => p.qc_status !== "approved");
  const noInv = plans.every((p) => !p.invoices.length);
  const mixed = !noInv && plans.some((p) => !p.invoices.length);
  const go = async () => {
    if (!tr) return toast.error("Pick the transporter");
    setBusy(true);
    try {
      const n = (v) => (v === "" || v == null ? null : Number(v));
      const byLm = {};  // several plans of one LM move as one dispatch: serial rows are merged per LM line
      plans.forEach((p) => p.lines.forEach((l) => {
        const rows = cleanSerials(serials[`${p.id}|${l.line_item_id}`]);
        if (rows.length) { byLm[p.lm_id] = byLm[p.lm_id] || {}; byLm[p.lm_id][l.line_item_id] = [...(byLm[p.lm_id][l.line_item_id] || []), ...rows]; }
      }));
      const r = await api.post("/movements/from-plans", { ...f, plan_ids: plans.map((p) => p.id), transporter_id: tr.id, serials: byLm,
        estimated_freight: n(est.freight), estimated_loading: n(est.loading), estimated_unloading: n(est.unloading) });
      toast.success(`${r.data.movement_no} created with ${r.data.dispatch_nos.join(", ")}`);
      promptDispatchAssets(r.data.asset_prompts, navigate);
      onDone(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="movement-from-plans-dialog">
        <DialogHeader><DialogTitle>Create movement from {plans.length} planned dispatch{plans.length > 1 ? "es" : ""}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">LMs, addresses, items, quantities and invoices come from the plans. Only add the transport details and your cost estimate.</p>
        {notQc.length > 0 && <p className="text-xs rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800" data-testid="mfp-qc-block">Movement blocked: {notQc.map((p) => `${p.planned_dispatch_number} (${p.qc_status.replace(/_/g, " ")})`).join(", ")}. Only QC-approved planned dispatches can move.</p>}
        <Summary plans={plans} dc={noInv} serials={serials} setSerials={setSerials} />
        {mixed && <p className="text-xs rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800" data-testid="mfp-mixed-warning">Some selected plans have no LM invoice. Link the invoice on those plans, or move them on a separate delivery-challan movement.</p>}
        <div><Label>Transporter *</Label><EntityPicker url="/transporters?active=true" value={tr} onChange={setTr} render={trRender} placeholder="From the Transporters master" testid="mfp-transporter" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l, t]) => <div key={k}><Label>{l}</Label><Input type={t || "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`mfp-${k}`} /></div>)}
          {noInv && <div><Label>Delivery challan no.</Label><div className="h-9 rounded border border-dashed border-slate-300 px-2 py-2 text-xs text-slate-500" data-testid="mfp-dc-auto">Issued automatically (TES-DC-YYMM-NNN)</div></div>}
          {noInv && <ChallanFields value={f} onChange={setF} testid="mfp-challan" />}
        </div>
        <BudgetCompare plans={plans} est={est} setEst={setEst} />
        <DialogFooter><Button disabled={busy || mixed || notQc.length > 0} onClick={go} data-testid="mfp-create-btn">Create movement</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
